import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Trash2, Loader2, Plus, Pencil } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const resolvePhoto = (p) => {
  if (!p) return null;
  if (p.startsWith("http")) return p;
  return `${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`;
};

export default function WishlistsPage() {
  const { user } = useAuth();
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [newName, setNewName] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/me/wishlists");
      setLists(data || []);
    } catch (_) {} finally { setLoading(false); }
  };
  useEffect(() => { if (user) load(); else setLoading(false); }, [user]);

  const create = async () => {
    const name = (newName || "Nouvelle liste").trim();
    try {
      const { data } = await api.post("/me/wishlists", { name });
      setLists((arr) => [data, ...arr]);
      setNewName("");
      toast.success(`Liste « ${data.name} » créée`);
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const rename = async (wl, name) => {
    try {
      await api.patch(`/me/wishlists/${wl.id}`, { name: name.trim() });
      setLists((arr) => arr.map((w) => w.id === wl.id ? { ...w, name: name.trim() } : w));
      setEditing(null);
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const del = async (wl) => {
    if (!window.confirm(`Supprimer la liste « ${wl.name} » ?`)) return;
    try {
      await api.delete(`/me/wishlists/${wl.id}`);
      setLists((arr) => arr.filter((w) => w.id !== wl.id));
      toast.success("Liste supprimée");
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  if (!user) {
    return (
      <div className="pt-32 max-w-3xl mx-auto px-4 pb-24 text-center">
        <h1 className="font-display text-3xl font-bold">Connectez-vous</h1>
        <p className="text-sm text-neutral-600 mt-2 mb-6">Créez un compte pour organiser vos coups de cœur.</p>
        <Link to="/login" className="inline-block bg-[#0A0A0A] text-white px-6 py-3 font-semibold">Se connecter</Link>
      </div>
    );
  }

  return (
    <div className="pt-24 sm:pt-28 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24" data-testid="wishlists-page">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#7A1535]">Coups de cœur</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mt-2">Mes listes</h1>
          <p className="text-sm text-neutral-600 mt-2 max-w-xl">Organisez vos pépites bretonnes par voyage, par saison, par humeur.</p>
        </div>
      </div>

      {/* Quick create */}
      <div className="bg-[#F5F2EC] border border-black/10 p-4 sm:p-5 mb-8 flex flex-col sm:flex-row gap-3" data-testid="wl-quick-create">
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && create()}
          placeholder="Nommez une nouvelle liste (Été 2026, En amoureux…)"
          className="flex-1 bg-white border border-black/15 px-3 py-2 text-sm"
        />
        <button onClick={create} className="px-5 py-2 bg-[#0A0A0A] text-white text-sm font-semibold inline-flex items-center justify-center gap-2" data-testid="wl-page-create">
          <Plus className="w-4 h-4" /> Créer
        </button>
      </div>

      {loading ? (
        <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : lists.length === 0 ? (
        <div className="text-center py-16">
          <Heart className="w-12 h-12 mx-auto text-neutral-300 mb-3" />
          <p className="text-neutral-500">Aucune liste pour le moment.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {lists.map((wl) => (
            <div key={wl.id} className="group" data-testid={`wl-card-${wl.id}`}>
              <Link to={`/wishlists/${wl.id}`} className="block aspect-square rounded-lg overflow-hidden bg-[#F5F2EC] relative">
                <CoverCollage previews={wl.preview} />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
              </Link>
              <div className="flex items-start justify-between gap-2 mt-3">
                <div className="flex-1 min-w-0">
                  {editing === wl.id ? (
                    <input
                      defaultValue={wl.name}
                      autoFocus
                      onKeyDown={(e) => { if (e.key === "Enter") rename(wl, e.currentTarget.value); if (e.key === "Escape") setEditing(null); }}
                      onBlur={(e) => rename(wl, e.currentTarget.value)}
                      className="w-full border-b border-[#0A0A0A] outline-none font-display text-lg font-semibold bg-transparent"
                    />
                  ) : (
                    <div className="font-display text-lg font-semibold truncate">{wl.name}</div>
                  )}
                  <div className="text-xs text-neutral-500">{wl.count} logement{wl.count !== 1 ? "s" : ""}</div>
                </div>
                <button onClick={() => setEditing(wl.id)} className="p-1.5 opacity-0 group-hover:opacity-100 hover:bg-neutral-100 transition-opacity" aria-label="Renommer">
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => del(wl)} className="p-1.5 opacity-0 group-hover:opacity-100 hover:bg-neutral-100 text-[#9A4D1F] transition-opacity" aria-label="Supprimer" data-testid={`wl-del-${wl.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CoverCollage({ previews }) {
  const items = (previews || []).slice(0, 3);
  if (items.length === 0) {
    return (
      <div className="w-full h-full flex items-center justify-center text-neutral-300">
        <Heart className="w-12 h-12" />
      </div>
    );
  }
  if (items.length === 1) {
    return <img src={resolvePhoto(items[0].photos?.[0])} alt="" className="w-full h-full object-cover" />;
  }
  return (
    <div className="w-full h-full grid grid-cols-2 gap-0.5">
      <img src={resolvePhoto(items[0].photos?.[0])} alt="" className="row-span-2 w-full h-full object-cover" />
      <img src={resolvePhoto(items[1].photos?.[0])} alt="" className="w-full h-full object-cover" />
      {items[2] ? <img src={resolvePhoto(items[2].photos?.[0])} alt="" className="w-full h-full object-cover" /> : <div className="bg-neutral-200" />}
    </div>
  );
}
